import { createFileRoute, redirect } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { hasModule, firstAllowedRoute } from "@/lib/auth";
import { OwnerSettingsClient } from "@/features/warehouse/SettingsClient";
import { WarehousePicker, useWarehouseChoice } from "@/features/warehouse/WarehouseParts";

// Warehouse → Instellingen, one owner in one warehouse with the warehouse's value beside each
// setting (docs/design_handoff_wms/README.md §4.3).
export const Route = createFileRoute("/_shell/warehouse/settings/$ownerId")({
  beforeLoad: ({ context }) => {
    if (!hasModule(context.session, "wms_masterdata") && !hasModule(context.session, "wms_masterdata_write")) {
      throw redirect({ to: firstAllowedRoute(context.session) });
    }
  },
  validateSearch: (search: Record<string, unknown>): { warehouse?: string } => ({
    warehouse: typeof search.warehouse === "string" && search.warehouse ? search.warehouse : undefined,
  }),
  component: OwnerSettingsScreen,
});

function OwnerSettingsScreen() {
  const { t } = useTranslation();
  const { ownerId } = Route.useParams();
  const search = Route.useSearch();
  const navigate = Route.useNavigate();
  const { session } = Route.useRouteContext();
  const choice = useWarehouseChoice(search.warehouse, (code) => navigate({ search: { warehouse: code }, replace: true }));
  const warehouse = choice.current;

  return (
    <div className="wl-page" data-screen-label="Back-office Warehouse Owner Settings">
      {!warehouse ? (
        <p className="t-body-sm t-muted">{choice.isLoading ? t("common.loading") : t("warehouse.noWarehouses")}</p>
      ) : (
        <OwnerSettingsClient
          warehouse={warehouse}
          ownerId={ownerId}
          picker={<WarehousePicker warehouses={choice.warehouses} current={warehouse} onChoose={choice.choose} />}
          canEdit={session.roles.includes("admin")}
          onOpenOwner={(id) => navigate({ to: "/warehouse/settings/$ownerId", params: { ownerId: id }, search: { warehouse: warehouse.code } })}
          onOpenMatrix={() => navigate({ to: "/warehouse/settings", search: { warehouse: warehouse.code } })}
        />
      )}
    </div>
  );
}
